import React, { MutableRefObject, useEffect, useState, useRef } from 'react';
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import humanizeDuration from 'humanize-duration';
import { Formik, Form, FormikHelpers as FormikActions } from 'formik';

import { TooManyRequests, cleanupWs, useWebsocket } from "django-rest-react";
import { nuovo_punteggio_vuoto, useEvento, useLogin, useProblema, usePunteggi, usePunteggiMarkov } from '../reducers';
import { FormGroupBootstrap, ConfirmedButton } from '../common-components'
import {
  TabellaPunteggi, GraficoPunteggi, MappaLeaflet,
  CountdownClock, ThrottleToast,
  MessaggiImportantiList, IstogrammaPunteggi, IstogrammaPunteggi2,
  TabellaPunteggiMinimal, RispostaFeed, DisplayOrario, Latency, LoadingCircle, FlipClockInternal,
  MappaPoli,
} from '../components';
import { set_tutti_punteggi_action, set_punteggi_squadre_action, set_markov_stato, set_punteggio_action, change_parameters, add_punteggi_evento_action } from '../actions'
import {
  get_tutti_punteggi, parse_classifica,
  refresh_problemi, get_squadre_sessione,
  get_markov_stato, dispatchable_score, get_sessione_by_pk,
  get_evento_by_pk, connectPunteggiWs, connectPunteggiEventiWs, get_pk_risposte,
} from '../api';
import {
  Evento, Polo, PunteggiElaborati,
  SquadraInSessione,
  TunableParametersForm as TunableParameters
} from '../api/types';
import store from '../store';
import { nice_date, nice_date_discorsiva, range } from '../utils'
import { semplificato } from '../globals'
import { get_polo, get_risposta_senza_valore } from '../api/auto-apis';
import BigliettiniConsegna from '../components/BigliettiniConsegna';


const pageMatch = useParams<{ pk: string, scuola_id?: string }>;

type Sessione = Awaited<ReturnType<typeof get_sessione_by_pk>>
type Squadra = Awaited<ReturnType<typeof get_squadre_sessione>>[0]


const filterPunteggiTimestamp = (allPunteggi: ReturnType<typeof usePunteggi>, currentTs: Date, replayMode: boolean) => {
  if (!replayMode) return allPunteggi.states;

  return allPunteggi.states.filter(state => state.ora.getTime() < currentTs.getTime());
}

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/Window/setTimeout#maximum_delay_value
 * Delay is in int32, this means that after 24days the timeout flips.
 * We will not be ok with someone keeping the page alive for 20 days before the event.
 */
const setTimeoutLongIgnored: (callback: () => void, delayMs: number) => number = (callback, delayMs) => {
  // 2**30, that is 12 days
  if (delayMs > 1073741824) {
    return -1;
  }
  return window.setTimeout(callback, delayMs)
}


const extraProps = (
  evento: ReturnType<typeof useEvento>[0],
  allPunteggi: ReturnType<typeof usePunteggi>,
  currentTs: Date,
  replayMode: boolean) => {


  const inizio = new Date(Date.parse(evento.inizio));
  const fine = new Date(Date.parse(evento.fine));

  const squadra_punteggi_storia = allPunteggi || nuovo_punteggio_vuoto;

  const storia = filterPunteggiTimestamp(squadra_punteggi_storia, currentTs, replayMode);
  const punteggi = storia[0];
  const punteggi_problemi = punteggi?.punteggi_problemi || [];

  return {
    storia: storia,
    inizio: inizio,
    fine: fine,
    punteggi: punteggi,
    punteggi_problemi: punteggi_problemi,
  }
}


const classifica_semp = (classifica: PunteggiElaborati["classifica"], scuole_id: number[], squadre: SquadraInSessione[], soglia: number | null) => {
  return classifica.filter(squadra => {
    if (!semplificato(squadra.posizione)) return true;
    const whitelist = [50, 100, 150, 200, 300, 400, 500];
    if (!!soglia) {
      whitelist.splice(0, 0, soglia);
    }
    if (whitelist.includes(squadra.posizione)) return true;
    if (scuole_id.includes(squadre.find(sq => sq.id == squadra.id)?.scuola.id as number)) return true;
    return false;
  })
}


const classifica_polo = (classifica: PunteggiElaborati["classifica"], polo: number, squadre: SquadraInSessione[], soglia: number | null) => {
  return classifica.filter(squadra => {
    const whitelist = range(20).map(val => val + 1);
    if (!!soglia) {
      whitelist.splice(whitelist.length, 0, soglia);
    }
    if (whitelist.includes(squadra.posizione)) return true;
    const sq = squadre.find(sq => sq.id === squadra.id);
    if (!sq) return false;
    return sq.polo === polo;
  })
}


const get_timer = (inizio: Date, fine: Date, stato: StatoEvento, currentTs: Date, replayMode: boolean) => {
  if (!(inizio && fine)) {
    return null
  }

  if (replayMode) {
    const totalSeconds = Math.floor((fine.getTime() - currentTs.getTime()) / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return (
      <div className="d-flex justify-content-center">
        <FlipClockInternal
          days={days}
          hours={hours}
          minutes={minutes}
          seconds={seconds}
        />
      </div>
    )
  }

  let fine_countdown;
  // Non dovrebbe esserci bisogno di settare un countdown
  // per ri-renderizzare quando si passa l'inizio, in quanto
  // render verrà chiamata di nuovo sicuramente con l'autorefresh
  // dei punteggi.
  if (stato == StatoEvento.DeveIniziare) {
    fine_countdown = inizio;
  } else {
    fine_countdown = fine;
  }
  if (stato == StatoEvento.Finito) {
    return null;
  }
  const nome = stato == StatoEvento.InCorso ? "fine" : "inizio";
  return (
    <div className="d-flex justify-content-center">
      <CountdownClock
        id={`sessione`}
        key={nome}
        fine={fine_countdown}
      />
    </div>);
}


const get_testo_blocco = (adesso: Date, inizio: Date, fine: Date) => {
  let testo, blocco_fine;
  if (inizio) {
    if (adesso < inizio) {
      testo = `L'evento non è ancora iniziato. Inizierà ${nice_date_discorsiva(inizio)} e durerà ${humanizeDuration(fine.getTime() - inizio.getTime(), { language: "it" })}.`;
    } else if (adesso > fine) {
      testo = `L'evento si è concluso ${nice_date_discorsiva(fine)} ed è durato ${humanizeDuration(fine.getTime() - inizio.getTime(), { language: "it" })}.`;
      blocco_fine = (
        <h1 className="text-center text-warning py-2">La gara è terminata!</h1>
      )
    } else {
      testo = `L'evento è in corso e finirà ${nice_date_discorsiva(fine)}`;
    }
  }
  return [testo, blocco_fine];
}


const refreshProblemi = (evento: Evento, timeoutSetter: (val: number) => void) => {
  const inizio = new Date(evento.inizio)
  const adesso = new Date();
  if (inizio > adesso) {
    // Set timer che scatta 3 secondi dopo l'inizio
    const timeout = setTimeoutLongIgnored(() => {
      refreshProblemi(evento, timeoutSetter);
    }, inizio.getTime() - adesso.getTime() + 1000)
    timeoutSetter(timeout);
  } else {
    refresh_problemi(evento.id);
  }
}


enum StatoEvento {
  DeveIniziare,
  InCorso,
  Finito,
}


const getBloccoSlider = (path: string, sessione_pk: number, evento: Evento) => {
  const rex = new RegExp("/sessione/([0-9]+)/punteggi/standard-model/");
  if (!path.match(rex)) return null;
  const FIELDS = [
    "MAX_ERRORI_RAISE_MONTEPREMI", "ERRORE_PUNTEGGI_RAISE", "PTI_TOLTI_ERRORE",
    "PTI_BASE_MONTEPREMI", "BONUS_VELOCITA", "SCALING_BONUS_VELOCITA",
    "SCALING_BONUS_MONTEPREMI",
  ] as (keyof Omit<TunableParameters, "PUNTEGGIO_BASE_PROBLEMI">)[];

  let initial = { PUNTEGGIO_BASE_PROBLEMI: 30 } as TunableParameters;
  if (evento != null) {
    for (const key of FIELDS) {
      const value = evento[key];
      if (typeof value === "number") {
        initial[key] = value
      } else {
        initial[key] = parseFloat(value);
      }
    }
  }

  const onSubmit = (values: TunableParameters, actions: FormikActions<TunableParameters>) => {
    store.dispatch(change_parameters(sessione_pk, values))
    const last = store.getState().punteggi_markov[sessione_pk.toString()]
    store.dispatch(set_punteggio_action(dispatchable_score(last), sessione_pk))
    actions.setSubmitting(false);
  }
  return (
    <div className="card bg-light-primary">
      <div className="card-body">
        <h4 className="card-title">Opzioni sviluppatore</h4>
        <p>Questa sezione permette di sperimentare parametri diversi nel sistema di punteggi. Non modifica in alcun modo i veri risultati della gara.</p>

        <Formik
          onSubmit={onSubmit}
          initialValues={initial}
          enableReinitialize
        >
          {({ handleReset, isSubmitting }) => {
            return (<Form className="needs-validation">
              <FormGroupBootstrap
                name="MAX_ERRORI_RAISE_MONTEPREMI" type="number" step={1}
              />
              <FormGroupBootstrap
                name="ERRORE_PUNTEGGI_RAISE" type="number" step={1}
              />
              <FormGroupBootstrap
                name="PTI_TOLTI_ERRORE" type="number" step={1}
              />
              <FormGroupBootstrap
                name="PTI_BASE_MONTEPREMI" type="number" step={1}
              />
              <FormGroupBootstrap
                name="PUNTEGGIO_BASE_PROBLEMI" type="number" step={1}
              />
              <FormGroupBootstrap
                name="BONUS_VELOCITA" type="number" step={1}
              />
              <FormGroupBootstrap
                name="SCALING_BONUS_VELOCITA" type="number" step={0.001}
              />
              <FormGroupBootstrap
                name="SCALING_BONUS_MONTEPREMI" type="number" step={0.001}
              />
              <div className="d-flex justify-content-end">
                <ConfirmedButton type="warning" onSubmit={handleReset}>Reset</ConfirmedButton>
                <button disabled={isSubmitting} className="btn btn-success" type="submit">Ricalcola</button>
              </div>

            </Form>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}


const loadEventInfos = async (
  pk: number,
  setSessione: ReturnType<typeof useState<Sessione | null>>[1],
  setEvento: ReturnType<typeof useState<Evento | null>>[1],
  setSquadre: ReturnType<typeof useState<Squadra[]>>[1],
  setThrottleError: ReturnType<typeof useState<any>>[1],
  setStatoEvento: ReturnType<typeof useState<StatoEvento>>[1],
  setPoli: ReturnType<typeof useState<Polo[]>>[1],
  wsPunteggi: MutableRefObject<any>,
  wsEventi: MutableRefObject<any>,
  timeouts: number[],
  timers: number[],
) => {
  const sessione = await get_sessione_by_pk(pk);
  setSessione(sessione);
  document.title = `Punteggi ${sessione.nome} - GaS`;

  const evento = await get_evento_by_pk(sessione.evento);
  setEvento(evento);

  // Non dobbiamo necessariamente essere blocking su questo
  // ma ci serve l'evento e quindi dobbiamo partire da qua
  get_polo({ evento: evento.id }).then(setPoli);

  // Prendiamo le squadre
  const squadre = await get_squadre_sessione(pk)
  setSquadre(squadre)

  // Stato dell'evento
  const adesso = new Date();
  const inizio = new Date(evento.inizio);
  const fine = new Date(evento.fine);
  if (adesso > inizio) {
    refresh_problemi(evento.id);
    if (adesso < fine) {
      timeouts.push(setTimeoutLongIgnored(() => setStatoEvento(StatoEvento.Finito), fine.getTime() - adesso.getTime()))
      // Ogni 5 minuti
      timers.push(window.setInterval(() => fixMissing(pk), 5 * 60 * 1000))
      setStatoEvento(StatoEvento.InCorso)
    } else {
      setStatoEvento(StatoEvento.Finito)
    }
  } else {
    timeouts.push(setTimeoutLongIgnored(() => setStatoEvento(StatoEvento.InCorso), inizio.getTime() - adesso.getTime()))
    timeouts.push(setTimeoutLongIgnored(() => {
      refresh_problemi(evento.id)
      // Ogni 5 minuti
      timers.push(window.setInterval(() => fixMissing(pk), 5 * 60 * 1000))
    }, inizio.getTime() - adesso.getTime() + 500))
    setStatoEvento(StatoEvento.DeveIniziare)
  }


  const tante = semplificato(squadre.length)
  // Se sono poche, refresh lato backend e tutti i punteggi
  if (!tante) {

    connectPunteggiWs(wsPunteggi.current, pk);
    try {
      const json = await get_tutti_punteggi(pk);
      const nuovo = json.map(item => parse_classifica(item));
      store.dispatch(set_tutti_punteggi_action(nuovo, pk));
    } catch (err) {
      if (err instanceof TooManyRequests) {
        setThrottleError(err)
      } else {
        throw err;
      }
    }
  }

  // In ogni caso sottoscriviamo al feed degli eventi, sia se sono poche che tante
  // ma viene usato per il calcolo punteggi solo se sono poche.
  store.dispatch(set_punteggi_squadre_action(pk, evento, squadre, 40))
  // Irrilevante mettere un solo problema tanto adesso facciamo il fetch dello stato
  // che sa il numero giusto
  get_markov_stato(pk).then(stato => {
    store.dispatch(set_markov_stato(pk, evento, stato))
    const state = store.getState().punteggi_markov[pk]
    if (tante) {
      store.dispatch(set_punteggio_action(dispatchable_score(state), pk))
    }
    connectPunteggiEventiWs(wsEventi.current, pk, !tante);
  })
}

const fixMissing = async (pk: number) => {
  const allPunteggi = store.getState().punteggi_markov[pk]
  const client1 = allPunteggi.eventi.filter(ev => ev.kind === "r").map(ev => ev.id)
  // Se non abbiamo risposte in locale vuol dire che si è appena refreshato,
  // non ha senso fare questa cosa perché non possiamo calcolare il minimo pk

  // In alternativa, se stiamo usando il sistema a full refresh con poche squadre,
  // non ha senso la funzione fixMissing perché ottieniamo lo stato intero dal server.
  // Tuttavia possiamo chiamarla senza sofferenza perché nuovamente la lista degli eventi sarà vuota
  // e quindi terminiamo subito.
  if (client1.length == 0) {
    return;
  }

  // Chiediamo al server i pks solo se abbiamo jumps nei pk
  // Se non ci sono, non vale la pena di caricarlo.

  // La lista ha almeno un elemento
  let last_seen = client1[0];
  let consecutivi = true;
  for (const elem of client1.slice(1, 0)) {
    if (elem != last_seen + 1) {
      consecutivi = false;
      break;
    }
    last_seen = elem;
  }
  if (consecutivi) {
    console.log("Tutti pk consecutivi, non chiedo al server");
    return;
  }

  // Vogliamo fetchare solo quelli dopo il primo, perché se uno fa F5 della pagina non
  // vuoi recuperare quelli più vecchi che sono già inclusi nello stato
  const idMin = Math.min(...client1);
  // Allo stesso modo vogliamo prendere solo quelli precedenti all'ultimo,
  // perché gli altri arriveranno da soli
  const idMax = Math.max(...client1)

  const pks = await get_pk_risposte(pk);
  const server = pks.filter(pk => pk >= idMin && pk <= idMax);
  const client = new Set(client1);
  const missing = server.filter(val => !client.has(val));
  console.log("Checked server, missing ", missing);
  if (missing.length == 0) {
    return;
  }

  if (missing.length > 5) {
    console.warn("Missing more than 5 events!", missing);
  }
  await get_risposta_senza_valore({ pk__in: missing }).then(risps => {
    for (const risp of risps) {
      store.dispatch(add_punteggi_evento_action(pk, {
        problema: risp.problema, squadra: risp.squadra,
        corretto: risp.corretto, kind: "r",
        date: new Date(Date.parse(risp.momento_consegna)),
        id: risp.id,
      }))
    }
  })
}


interface ReplayFormValues {
  durata: number
}


interface ReplayProps {
  setCurrentTs: ReturnType<typeof useState<Date>>[1],
  setReplayMode: ReturnType<typeof useState<boolean>>[1],
  replayMode: ReturnType<typeof useState<boolean>>[0],
  inizio: Date,
  fine: Date,
}

const SetupReplay = (props: ReplayProps) => {
  const { setCurrentTs, setReplayMode, replayMode, inizio, fine } = props;

  const [showReplay, setShowReplay] = useState<boolean>(false);
  const replayInterval = useRef<number | null>(null);

  return (
    <>
      <div className="d-flex justify-content-center">
        <button className="btn btn-info" type="button" onClick={() => setShowReplay(prev => !prev)}>
          <i className="fa fa-video" /> {showReplay && "Nascondi "} {!showReplay && "Mostra "} replay
        </button>
      </div>

      {showReplay &&
        <Formik
          initialValues={{
            durata: 120,
          }}
          onSubmit={(values: ReplayFormValues, actions: FormikActions<ReplayFormValues>) => {
            setReplayMode(true);
            setCurrentTs(inizio);
            const REPLAY_TICK_MS = 50;
            const replay_ticks = values.durata * 1000 / REPLAY_TICK_MS;
            const replay_tick_real_duration_ms = (fine.getTime() - inizio.getTime()) / replay_ticks;

            replayInterval.current = window.setInterval(() => {
              setCurrentTs((cur: Date) => {
                if (cur.getTime() > fine.getTime()) {
                  setReplayMode(false);
                  if (!!replayInterval.current) window.clearInterval(replayInterval.current);
                }
                return new Date(cur.getTime() + replay_tick_real_duration_ms)
              })

            }, REPLAY_TICK_MS)
            actions.setSubmitting(false);
          }}
        >
          {({ isSubmitting }) => {

            return (
              <Form className="form needs-validation">
                <FormGroupBootstrap
                  displayName="Durata"
                  name="durata" type="number" step={1}
                  help_text={"Vedrai un replay di durata in secondi pari al numero scelto in questo campo."}
                />
                <div className="d-flex justify-content-end">
                  <button className="btn btn-success" type="submit" disabled={isSubmitting || replayMode}>
                    Start replay
                  </button>
                </div>
              </Form>
            )
          }}
        </Formik>
      }
    </>
  )
}


const Punteggi = (props: {}) => {
  const [squadre, setSquadre] = useState([] as Squadra[]);
  const [poli, setPoli] = useState<Polo[]>([]);
  const [statoEvento, setStatoEvento] = useState(StatoEvento.DeveIniziare);
  const [throttleError, setThrottleError] = useState(null);
  const [evento, setEvento] = useState<Evento | null>(null);
  const [sessione, setSessione] = useState<Sessione | null>(null);
  const params = pageMatch()
  const location = useLocation();
  const navigate = useNavigate();
  const [tabKey, setTabKey] = useState(location.hash.split('#')[1]);

  const [replayMode, setReplayMode] = useState<boolean>(false);
  const [currentTs, setCurrentTs] = useState<Date>(new Date());


  const session_pk = parseInt(params.pk as string);
  const login = useLogin();
  const allPunteggi = usePunteggi(session_pk);
  const dettagli_problemi = useProblema(evento?.id);


  const wsPunteggi = useWebsocket();
  const wsEventi = useWebsocket();


  useEffect(() => {
    const base = location.hash.split("#")[1]?.split("?")[0];
    const newval = (base?.length === 0 || base?.length === undefined) ? "tabella" : base;
    setTabKey(newval);
  }, [location.hash])

  useEffect(() => {
    const timeouts = [] as number[];
    const timers = [] as number[];
    loadEventInfos(
      session_pk, setSessione, setEvento,
      setSquadre, setThrottleError, setStatoEvento, setPoli,
      wsPunteggi, wsEventi, timeouts, timers).catch(err => console.error("Errore cusumano", err))
    return () => {
      timeouts.forEach(val => {
        if (val) clearTimeout(val)
      })
      timers.forEach(val => {
        if (val) clearTimeout(val)
      })
      cleanupWs(wsPunteggi.current)
      cleanupWs(wsEventi.current)
    }
  }, [])

  if (!evento || !sessione) {
    return <LoadingCircle />
  }
  const adesso = new Date();
  const gara_con_poli = squadre.some(sq => !!sq.polo) && poli.length > 0;

  const show_bigliettini = gara_con_poli && login.logged_in && (login.user.is_superuser || poli.some(polo => polo.responsabili.some(resp => resp.id === login.user.responsabile.id)));

  const { inizio, fine, punteggi, storia } = extraProps(evento, allPunteggi, currentTs, replayMode);
  const ultimo: PunteggiElaborati = punteggi || {
    ora: null,
    punteggi_problemi: [],
    classifica: [],
  };

  let nomi: { [squadra: string]: string; } = {};
  for (const sq of squadre) {
    nomi[sq.nome] = sq.ospite ? sq.nome + ' (Ospite)' : sq.nome;
  }

  const timer = get_timer(inizio, fine, statoEvento, currentTs, replayMode);
  if (location.pathname.includes("telecronaca")) {
    return (
      <>
        <div className="flex-row">
          <TabellaPunteggiMinimal
            storia={storia}
            punteggi={ultimo.punteggi_problemi}
            dettagli_problemi={dettagli_problemi}
            classifica={ultimo.classifica}
          />
          <RispostaFeed
            problemi={dettagli_problemi} squadre={squadre} timer={timer}
            session_pk={session_pk}
          />
        </div>
        <br />
        <br />
        <DisplayOrario evento={evento} />
        {!semplificato(squadre.length) && adesso.getTime() > fine.getTime() &&
          <SetupReplay setCurrentTs={setCurrentTs} setReplayMode={setReplayMode} replayMode={replayMode} inizio={inizio} fine={fine} />
        }
      </>
    )
  }
  const [testo, blocco_fine] = get_testo_blocco(new Date(), inizio, fine)
  const error_toast = throttleError ? <ThrottleToast obj={this} /> : null;


  const semp = semplificato(ultimo.classifica.length);
  let scuole_id = login.logged_in ? login.user.responsabile.scuole : []
  const { numero_qualificati } = sessione;
  if (params.scuola_id) { scuole_id.push(parseInt(params.scuola_id)) }
  const classifica_semplificata = classifica_semp(ultimo.classifica, scuole_id, squadre, numero_qualificati);

  let blocco_slider = getBloccoSlider(location.pathname, session_pk, evento);


  return (
    <div className="container-fluid">
      {error_toast}
      <div style={{ paddingBottom: "30px", display: "flex" }}>
        <h1 className="page-header" style={{ marginTop: "10px", width: "33%" }}>{evento?.titolo} - sessione {sessione.nome}</h1>
        <div style={{ width: "66%", marginTop: "24px" }}>
          {ultimo.ora &&
            <p style={{ textAlign: "right", marginBottom: "0" }}>Ultimo aggiornamento: {nice_date(ultimo.ora)}</p>
          }
          <p style={{ textAlign: "right", marginBottom: "0" }}>
            Questa pagina si ricarica da sola. Il refresh manuale è consigliato solo in caso di problemi di visualizzazione.
          </p>
          <p style={{ textAlign: "right", marginBottom: "0" }}>{testo}</p>
        </div>
      </div>
      {timer}
      {!replayMode && blocco_fine}
      {blocco_slider}
      <Tabs
        activeKey={tabKey}
        onSelect={(k) => {
          navigate(`${location.pathname}${location.search}#${k}`)
          if (!!k) setTabKey(k)
        }}
        id="nav-tab"
        mountOnEnter={true}
        className="mb-3"
      >
        {semplificato(squadre.length) &&
          <Tab eventKey="tabella-semplificata" title="Classifica semplificata">
            <TabellaPunteggi
              punteggi={ultimo.punteggi_problemi}
              nomi={nomi}
              dettagli_problemi={dettagli_problemi}
              classifica={classifica_semplificata}
              scuole_id={scuole_id}
              squadre={squadre}
              soglia={numero_qualificati}
            />
          </Tab>
        }
        {gara_con_poli &&
          <Tab eventKey="classifica-poli" title="Classifica polo">
            <Formik
              initialValues={{ polo: poli[0]?.id }}
              onSubmit={() => {}}
            >
              {({ values }) => {
                const polo = parseInt(values.polo as unknown as string);
                return (
                  <>
                    <Form>
                      <FormGroupBootstrap name="polo" type="select" choices={poli} displayChoice={(polo: Polo) => polo.nome_breve} />
                    </Form>
                    <TabellaPunteggi
                      punteggi={ultimo.punteggi_problemi}
                      nomi={nomi}
                      dettagli_problemi={dettagli_problemi}
                      classifica={classifica_polo(ultimo.classifica, polo, squadre, numero_qualificati)}
                      scuole_id={scuole_id}
                      squadre={squadre}
                      soglia={numero_qualificati}
                      polo={polo}
                    />
                  </>
                )
              }}
            </Formik>
          </Tab>
        }
        <Tab eventKey="tabella" title="Classifica">
          <TabellaPunteggi
            punteggi={ultimo.punteggi_problemi}
            nomi={nomi}
            dettagli_problemi={dettagli_problemi}
            classifica={ultimo.classifica}
            scuole_id={scuole_id}
            squadre={squadre}
            soglia={numero_qualificati}
          />
        </Tab>
        {!semp &&
          <Tab eventKey="plot" title="Grafico" >
            <GraficoPunteggi pk={session_pk} nomi={nomi} evento={evento} currentTs={currentTs} replayMode={replayMode} />
          </Tab>
        }
        <Tab eventKey="bar" title="Grafico a barre">
          <IstogrammaPunteggi evento={evento} classifica={ultimo.classifica} nomi={nomi} />
        </Tab>
        <Tab eventKey="bar2" title="Istogramma punteggi">
          <IstogrammaPunteggi2 evento={evento} classifica={ultimo.classifica} />
        </Tab>
        <Tab eventKey="map" title="Mappa">
          <MappaLeaflet
            punteggi={ultimo.classifica}
            squadre={squadre}
            nomi={nomi}
          />
        </Tab>
        {
          gara_con_poli &&
          <Tab eventKey="mappa-poli" title="Mappa poli">
            <MappaPoli squadre={squadre} evento={evento.id} />
          </Tab>
        }
        {gara_con_poli && show_bigliettini &&
          <Tab eventKey="bigliettini-consegna" title="Bigliettini consegna">
            <BigliettiniConsegna all_squadre={squadre} poli={poli} />
          </Tab>
        }
        <Tab eventKey="messages" title="Messaggi importanti">
          {evento && <MessaggiImportantiList evento={evento} />}
        </Tab>
        {
          login.user.is_superuser &&
          <Tab eventKey="latency" title="Latenza arrivo risposte">
            <Latency sessione={session_pk} />
          </Tab>
        }
      </Tabs>
      {!semplificato(squadre.length) && adesso.getTime() > fine.getTime() &&
        <SetupReplay setCurrentTs={setCurrentTs} setReplayMode={setReplayMode} replayMode={replayMode} inizio={inizio} fine={fine} />
      }
    </div>
  );
}

export default Punteggi;
